import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { IOSSwitch } from '../../components/boothly/ios-switch.jsx';
import { SaleNumpad } from '../../flea/v2/sale-numpad.jsx';
import { fmtCurrency, fmtPercent } from '../../lib/format.js';

// Preis-Kalkulator v2 — "Was-wäre-wenn"-Sheet (kein Backend, keine Persistenz).
// Layout nach docs/redesign/kalkulator-mockup.html: zentrierte Hero-Ergebniszone oben,
// Inset-Grouped-Eingaben, iOS-Slider, NumPad ohne eigenes Display (Wert läuft live in der Zeile).
// Zwei Modi: Preis→Gewinn und Gewinn→Preis. Akzent: Online.
//
// WICHTIG: Unterkomponenten (FieldRow, SliderRow, IOSSlider, …) stehen auf MODUL-Ebene.
// Würden sie im Render der Hauptkomponente definiert, bekäme React bei jeder State-Änderung
// einen neuen Komponententyp → kompletter Remount → der Slider verlöre mitten im Ziehen seinen
// Drag-State (Thumb folgt dann nicht dem Finger).

const MODE_P2G = 'p2g'; // Preis → Gewinn
const MODE_G2P = 'g2p'; // Gewinn → Preis
const IOS_EASE = 'cubic-bezier(0.32,0.72,0,1)';
const THUMB = 34; // px — muss mit der Thumb-Breite unten übereinstimmen

const LABELS = {
  vp:           'Verkaufspreis',
  zielgewinn:   'Zielgewinn / Stück',
  cogs:         'Einkaufspreis (EK)',
  versandEigen: 'Versandkosten (eigene)',
  kaeuferAnteil:'Anteil Käufer',
};

const EMPTY_VALS = { vp: null, zielgewinn: null, cogs: null, versandEigen: null };

// Wert aufs Raster runden (Float-Rauschen glätten).
function snapTo(v, step) {
  return Math.round((Math.round(v / step) * step) * 100) / 100;
}

const colorFor = (positive) => (positive ? 'var(--accent-green)' : 'var(--accent-red)');

const Sep = () => <div className="ml-4 h-px bg-[var(--separator)]" />;

// iOS-Slider — eigenes Pointer-Handling (kein Radix), damit der Thumb dem Finger
// KONTINUIERLICH folgt und die Füllung exakt bis zur Thumb-Mitte reicht (keine Lücke).
// Während des Ziehens läuft die Position frei, der WERT wird aufs Raster (snapStep) gesnapped;
// nach dem Loslassen rastet der Thumb auf den gesnappten Wert ein.
function IOSSlider({ value, min, max, snapStep, onValueChange, ariaLabel, testid }) {
  const trackRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [dragPct, setDragPct] = useState(0);

  const clampV = (v) => Math.min(max, Math.max(min, v));
  const valuePct = (max > min) ? (clampV(value) - min) / (max - min) : 0;
  const pct = drag ? dragPct : valuePct; // 0..1

  // Pointer-x → Anteil 0..1 (Thumb-Mitte bewegt sich im Bereich [THUMB/2, W−THUMB/2]).
  const pctFromX = (clientX) => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const usable = rect.width - THUMB;
    const x = clientX - rect.left - THUMB / 2;
    return usable > 0 ? Math.min(1, Math.max(0, x / usable)) : 0;
  };
  const emit = (p) => onValueChange(snapTo(min + p * (max - min), snapStep));

  const onPointerDown = (e) => {
    trackRef.current?.setPointerCapture?.(e.pointerId);
    setDrag(true);
    const p = pctFromX(e.clientX);
    setDragPct(p);
    emit(p);
  };
  const onPointerMove = (e) => {
    if (!drag) return;
    const p = pctFromX(e.clientX);
    setDragPct(p);
    emit(p);
  };
  const endDrag = (e) => {
    if (!drag) return;
    trackRef.current?.releasePointerCapture?.(e.pointerId);
    setDrag(false); // Thumb rastet auf gesnappten valuePct ein
  };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); onValueChange(snapTo(Math.min(max, clampV(value) + snapStep), snapStep)); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); onValueChange(snapTo(Math.max(min, clampV(value) - snapStep), snapStep)); }
  };

  // Thumb voll sichtbar (Inset THUMB/2 an den Enden); Füllung endet exakt unter der Thumb-Mitte.
  const thumbLeft = `calc((100% - ${THUMB}px) * ${pct})`;
  const fillWidth = `calc((100% - ${THUMB}px) * ${pct} + ${THUMB / 2}px)`;

  return (
    <div
      ref={trackRef}
      data-testid={testid}
      role="slider"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={clampV(value)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      className="relative h-[30px] w-full cursor-pointer touch-none select-none"
    >
      {/* Spur */}
      <div className="pointer-events-none absolute inset-x-0 top-1/2 h-[26px] -translate-y-1/2 overflow-hidden rounded-full bg-[var(--fill-tertiary)]">
        <div
          className="absolute left-0 top-0 h-full rounded-l-full bg-[var(--accent-online)]"
          style={{ width: fillWidth }}
        />
      </div>
      {/* Thumb */}
      <div
        className="pointer-events-none absolute top-1/2 h-[30px] w-[34px] -translate-y-1/2 rounded-[15px] bg-white shadow-[0_3px_10px_rgba(0,0,0,0.4)]"
        style={{ left: thumbLeft }}
      />
    </div>
  );
}

function FieldRow({ keyName, label, valueNum, indent, active, onOpen }) {
  return (
    <button
      type="button"
      data-testid={`kalk-field-${keyName}`}
      onClick={(e) => onOpen(keyName, e.currentTarget)}
      className={
        'flex min-h-[46px] w-full items-center justify-between px-4 py-3 text-left transition-colors duration-150 ' +
        (active ? 'bg-[var(--tint-online)]' : '')
      }
    >
      <span
        className={
          'text-[16px] ' +
          (indent ? 'text-[var(--label-secondary)]' : 'text-[var(--label-primary)]') +
          (active ? ' font-semibold' : '')
        }
      >
        {label}
      </span>
      <span
        className={
          'text-[16px] tabular-nums ' +
          (active
            ? 'font-semibold text-[var(--accent-online)]'
            : valueNum == null
              ? 'text-[var(--label-tertiary)]'
              : 'text-[var(--label-primary)]')
        }
      >
        {valueNum == null ? '—' : fmtCurrency(valueNum)}
      </span>
    </button>
  );
}

function SliderRow({ label, displayValue, valTestid, fieldKey, active, onOpen, value, min, max, snapStep, onChange, ariaLabel }) {
  return (
    <div className="px-4 pt-3 pb-3.5">
      <div className="mb-2.5 flex items-baseline justify-between">
        <span className="text-[16px] text-[var(--label-primary)]">{label}</span>
        <button
          type="button"
          data-testid={valTestid}
          onClick={(e) => onOpen(fieldKey, e.currentTarget)}
          className={
            'text-[16px] font-semibold tabular-nums text-[var(--accent-online)] ' +
            (active ? 'rounded-md bg-[var(--tint-online)] px-1.5' : '')
          }
        >
          {displayValue}
        </button>
      </div>
      <IOSSlider
        value={value}
        min={min}
        max={max}
        snapStep={snapStep}
        onValueChange={onChange}
        ariaLabel={ariaLabel}
        testid={`kalk-slider-${fieldKey}`}
      />
    </div>
  );
}

export function PreisKalkulatorSheet({ open, onOpenChange, dark }) {
  const [mode, setMode]                         = useState(MODE_P2G);
  const [vals, setVals]                         = useState(EMPTY_VALS);
  const [kaeuferZahltVersand, setKaeuferZahlt]  = useState(false);
  const [kaeuferAnteilAktiv, setKaeuferAnteilAktiv] = useState(false);
  const [kaeuferAnteil, setKaeuferAnteil]       = useState(null);
  const [gebuehrPct, setGebuehrPct]             = useState(0);
  const [fixGebuehr, setFixGebuehr]             = useState(0);
  const [menge, setMenge]                       = useState(1);
  const [activeField, setActiveField]           = useState(null);
  // Eingefrorener NumPad-Startwert: nur beim Feld-Öffnen gesetzt, nicht beim Tippen —
  // sonst flösse jeder Tastendruck über prefill zurück und das NumPad re-seedet.
  const [numpadSeed, setNumpadSeed]             = useState(0);
  // NumPad-Footer Ein-/Ausblenden: `closingPad` hält den Footer während der Schließ-Animation
  // gemountet, `padEntered` ist das Transition-Ziel (max-height/opacity) → ein einziges
  // weiches Aus-/Einfahren (Höhe kollabiert mit, kein abrupter Layout-Sprung am Ende).
  const [closingPad, setClosingPad]             = useState(false);
  const [padEntered, setPadEntered]             = useState(false);
  const [padH, setPadH]                         = useState(0); // gemessene NumPad-Inhaltshöhe
  const lastFieldRef = useRef(null);    // Feld, das während der Exit-Animation noch angezeigt wird
  const scrollTargetRef = useRef(null); // zuletzt angetipptes Element → in Sicht scrollen
  const scrollRef = useRef(null);       // scrollbarer Inhalt-Container
  const contentRef = useRef(null);      // innerer NumPad-Inhalt (für Höhenmessung)
  const footerRef = useRef(null);       // NumPad-Footer (für transitionend)

  // Frischer State bei jedem Öffnen.
  useEffect(() => {
    if (open) {
      setMode(MODE_P2G);
      setVals(EMPTY_VALS);
      setKaeuferZahlt(false);
      setKaeuferAnteilAktiv(false);
      setKaeuferAnteil(null);
      setGebuehrPct(0);
      setFixGebuehr(0);
      setMenge(1);
      setActiveField(null);
      setClosingPad(false);
      setPadEntered(false);
      setNumpadSeed(0);
    }
  }, [open]);

  // Öffnen: im nächsten Frame Transform-Slide ausfahren (translateY 100%→0).
  // Beim Schließen NICHT auf false setzen — dann bliebe der Slide; stattdessen kollabiert
  // `closingPad` die max-height (Box + belegter Platz fahren simultan nach unten weg).
  useEffect(() => {
    if (activeField !== null) {
      const r = requestAnimationFrame(() => setPadEntered(true));
      return () => cancelAnimationFrame(r);
    }
  }, [activeField]);

  // NumPad-Inhaltshöhe messen → sauberer max-height-Kollaps (von echter Höhe auf 0, kein Dead-Time).
  useLayoutEffect(() => {
    if (activeField !== null && contentRef.current) {
      const h = contentRef.current.scrollHeight;
      if (h && h !== padH) setPadH(h);
    }
  }, [activeField]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aktives Feld beim NumPad-Öffnen in Sicht scrollen. Ausgelöst durch `transitionend` des
  // Footers (deterministisch: erst wenn die Öffnen-Animation fertig ist, stimmt die Scroll-Höhe —
  // ein fixer Timer schlägt unter Last fehl). `scrollIntoView({block:'nearest'})` ist idempotent:
  // schon sichtbare Felder bewegen sich nicht (kein Wandern), verdeckte werden hereingeholt.
  useEffect(() => {
    if (activeField === null) return;
    const el = scrollTargetRef.current;
    const footer = footerRef.current;
    if (!el) return;
    let done = false;
    const doScroll = () => {
      if (done) return;
      done = true;
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    const onEnd = (e) => { if (e.target === footer) doScroll(); };
    footer?.addEventListener('transitionend', onEnd);
    const t = setTimeout(doScroll, 380); // Fallback: Feldwechsel ohne Transition / verschlucktes Event
    return () => { footer?.removeEventListener('transitionend', onEnd); clearTimeout(t); };
  }, [activeField]);

  if (!open) return null;

  // ── aktueller Wert eines NumPad-Ziels (für Seed) ───────────────────────────
  const currentVal = (key) => {
    switch (key) {
      case 'gebuehrPct':    return gebuehrPct;
      case 'fixGebuehr':    return fixGebuehr;
      case 'menge':         return menge;
      case 'kaeuferAnteil': return kaeuferAnteil ?? 0;
      default:              return vals[key] ?? 0;
    }
  };

  const openField = (key, el) => {
    scrollTargetRef.current = el ?? null;
    setClosingPad(false);
    lastFieldRef.current = key;
    setNumpadSeed(currentVal(key));
    // Frisches Öffnen (Footer war zu): Slide neu abspielen. Beim Feldwechsel (schon offen) nicht.
    if (activeField === null) setPadEntered(false);
    setActiveField(key);
  };

  // NumPad schließen: max-height kollabiert nach unten (Box + Platz simultan), dann unmount.
  const closeNumpad = () => {
    if (activeField === null) return;
    setClosingPad(true);
    setActiveField(null); // padEntered bleibt true → Transform hält, nur Höhe kollabiert
    setTimeout(() => setClosingPad(false), 320);
  };

  // ── NumPad-Eingabe ins aktive Ziel schreiben (Parent liest live mit) ───────
  const writeActive = (v) => {
    if (activeField === null) return; // während Exit-Animation nicht schreiben
    switch (activeField) {
      case 'gebuehrPct':    setGebuehrPct(v); break;
      case 'fixGebuehr':    setFixGebuehr(v); break;
      case 'menge':         setMenge(Math.max(1, Math.round(v))); break;
      case 'kaeuferAnteil': setKaeuferAnteil(v); break;
      default:              setVals((prev) => ({ ...prev, [activeField]: v }));
    }
  };

  const onToggleKaeufer = (checked) => {
    setKaeuferZahlt(checked);
    if (!checked) {
      setKaeuferAnteilAktiv(false);
      setKaeuferAnteil(null);
      if (activeField === 'kaeuferAnteil') closeNumpad();
    }
  };

  // ── Rechenlogik (leere Felder = 0) ─────────────────────────────────────────
  const f = gebuehrPct / 100;
  const F = fixGebuehr;
  const C = vals.cogs ?? 0;
  const S = vals.versandEigen ?? 0;
  const n = Math.max(1, Math.round(menge));
  const K = !kaeuferZahltVersand
    ? 0
    : (kaeuferAnteilAktiv ? (kaeuferAnteil ?? 0) : S);

  let headlineLabel, headlineValue, headlinePositive, marge, gesamt, gesamtPositive;

  if (mode === MODE_P2G) {
    const P = vals.vp ?? 0;
    const erloes = P + K;
    const G = (1 - f) * erloes - F - C - S;
    headlineLabel = 'Gewinn pro Stück';
    headlineValue = G;
    headlinePositive = G >= 0;
    marge = erloes > 0 ? (G / erloes) * 100 : null;
    gesamt = G * n;
    gesamtPositive = gesamt >= 0;
  } else {
    const Gz = vals.zielgewinn ?? 0;
    const denom = 1 - f;
    const Pneed = denom > 0 ? (Gz + C + S + F) / denom - K : NaN;
    const erloes = Pneed + K;
    headlineLabel = 'Nötiger Verkaufspreis';
    headlineValue = Pneed;
    headlinePositive = Pneed >= 0;
    marge = erloes > 0 ? (Gz / erloes) * 100 : null;
    gesamt = Gz * n;
    gesamtPositive = gesamt >= 0;
  }

  const numpadOpen = activeField !== null;
  const padMounted = numpadOpen || closingPad;
  const padField = activeField ?? lastFieldRef.current;
  const firstKey = mode === MODE_P2G ? 'vp' : 'zielgewinn';

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[20px]"
        style={{ animation: 'fadeIn 0.2s ease' }}
        onClick={() => onOpenChange(false)}
      />
      <div
        data-testid="sheet-preis-kalkulator"
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[92vh] flex-col rounded-t-3xl"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          animation: 'slideUpSheet 0.3s cubic-bezier(0.32,0.72,0,1) backwards',
          background: dark ? '#2C2C2E' : '#FFFFFF',
        }}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="bottom-sheet-handle" />
        </div>

        <div className="flex items-center justify-between px-5 pb-3">
          <h2 className="text-lg font-semibold">Preis-Kalkulator</h2>
          <button onClick={() => onOpenChange(false)} className="ghost-icon-btn" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modus-Segmented mit gleitendem Indikator */}
        <div className="px-5 pb-3">
          <div className="relative flex rounded-[9px] bg-[var(--fill-tertiary)] p-0.5">
            <div
              aria-hidden="true"
              className="absolute bottom-0.5 top-0.5 w-[calc(50%-2px)] rounded-[7px] bg-[var(--fill-primary)] shadow-sm"
              style={{
                left: '2px',
                transform: mode === MODE_P2G ? 'translateX(0)' : 'translateX(100%)',
                transition: `transform 0.3s ${IOS_EASE}`,
              }}
            />
            {[{ v: MODE_P2G, l: 'Preis → Gewinn' }, { v: MODE_G2P, l: 'Gewinn → Preis' }].map(({ v, l }) => (
              <button
                key={v}
                type="button"
                onClick={() => setMode(v)}
                className={
                  'relative z-10 flex-1 rounded-[7px] py-1.5 text-[13px] font-semibold transition-colors duration-200 ' +
                  (mode === v ? 'text-[var(--label-primary)]' : 'text-[var(--label-secondary)]')
                }
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollbarer Inhalt — min-h-0 ist Pflicht, damit der flex-1-Container unter seine
            Content-Höhe schrumpfen kann; sonst drückt er den NumPad-Footer unten aus dem
            Sheet (abgeschnitten) und ist nicht sauber scrollbar (Scroll-into-view greift nicht). */}
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-5 pb-6">
          {/* Hero (zentriert, keine Karte) */}
          <div
            className="text-center"
            style={{
              paddingTop: numpadOpen ? '0.25rem' : '0.5rem',
              paddingBottom: numpadOpen ? '0.875rem' : '1.5rem',
              transition: 'padding 0.2s ease',
            }}
          >
            <div
              data-testid="kalk-headline"
              className="font-bold leading-none tabular-nums"
              style={{
                fontFamily: 'ui-rounded, -apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: numpadOpen ? '38px' : '54px',
                transition: 'font-size 0.2s ease, color 0.2s ease',
                color: colorFor(headlinePositive),
              }}
            >
              {fmtCurrency(headlineValue)}
            </div>
            <div
              data-testid="kalk-headline-label"
              className="mt-0.5 text-[var(--label-secondary)]"
              style={{ fontSize: numpadOpen ? '13px' : '15px', transition: 'font-size 0.2s ease' }}
            >
              {headlineLabel}
            </div>
            {!numpadOpen && (
              <div className="mt-2.5 text-[13px] text-[var(--label-secondary)] tabular-nums">
                Marge{' '}
                <b
                  data-testid="kalk-marge"
                  className="font-semibold"
                  style={{ color: marge != null && marge < 0 ? 'var(--accent-red)' : 'var(--label-primary)' }}
                >
                  {fmtPercent(marge)}
                </b>
                <span className="mx-1.5 text-[var(--label-tertiary)]">·</span>
                {n} Stück{' '}
                <b data-testid="kalk-gesamt" className="font-semibold" style={{ color: colorFor(gesamtPositive) }}>
                  {fmtCurrency(gesamt)}
                </b>
              </div>
            )}
          </div>

          {/* Gruppe 1 — Beträge + Versand-Toggle */}
          <div className="overflow-hidden rounded-xl bg-[var(--fill-tertiary)]">
            <FieldRow keyName={firstKey} label={LABELS[firstKey]} valueNum={vals[firstKey]} active={activeField === firstKey} onOpen={openField} />
            <Sep />
            <FieldRow keyName="cogs" label={LABELS.cogs} valueNum={vals.cogs} active={activeField === 'cogs'} onOpen={openField} />
            <Sep />
            <FieldRow keyName="versandEigen" label={LABELS.versandEigen} valueNum={vals.versandEigen} active={activeField === 'versandEigen'} onOpen={openField} />
            <Sep />
            <div className="flex min-h-[46px] items-center justify-between px-4 py-2.5">
              <span className="text-[16px] text-[var(--label-primary)]">Versand zahlt Käufer</span>
              <IOSSwitch
                data-testid="kalk-toggle-kaeufer"
                checked={kaeuferZahltVersand}
                onCheckedChange={onToggleKaeufer}
              />
            </div>
            {kaeuferZahltVersand && !kaeuferAnteilAktiv && (
              <>
                <Sep />
                <div className="px-4 py-2.5">
                  <button
                    type="button"
                    data-testid="kalk-partial-link"
                    onClick={() => setKaeuferAnteilAktiv(true)}
                    className="text-[13px] font-medium text-[var(--accent-online)]"
                  >
                    Käufer zahlt nur einen Teil?
                  </button>
                </div>
              </>
            )}
            {kaeuferZahltVersand && kaeuferAnteilAktiv && (
              <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                <Sep />
                <FieldRow keyName="kaeuferAnteil" label="↳ Anteil Käufer" valueNum={kaeuferAnteil} active={activeField === 'kaeuferAnteil'} onOpen={openField} indent />
              </div>
            )}
          </div>

          {/* Gruppe 2 — Slider */}
          <div className="mt-4 overflow-hidden rounded-xl bg-[var(--fill-tertiary)]">
            <SliderRow
              label="Gebühr"
              displayValue={fmtPercent(gebuehrPct)}
              valTestid="kalk-sliderval-gebuehr"
              fieldKey="gebuehrPct"
              active={activeField === 'gebuehrPct'}
              onOpen={openField}
              value={gebuehrPct}
              min={0}
              max={25}
              snapStep={0.5}
              onChange={setGebuehrPct}
              ariaLabel="Gebühr in Prozent"
            />
            <Sep />
            <SliderRow
              label="Fixgebühr"
              displayValue={fmtCurrency(fixGebuehr)}
              valTestid="kalk-sliderval-fix"
              fieldKey="fixGebuehr"
              active={activeField === 'fixGebuehr'}
              onOpen={openField}
              value={fixGebuehr}
              min={0}
              max={2}
              snapStep={0.05}
              onChange={setFixGebuehr}
              ariaLabel="Fixgebühr in Euro"
            />
            <Sep />
            <SliderRow
              label="Stückzahl"
              displayValue={String(menge)}
              valTestid="kalk-sliderval-menge"
              fieldKey="menge"
              active={activeField === 'menge'}
              onOpen={openField}
              value={menge}
              min={1}
              max={100}
              snapStep={1}
              onChange={(v) => setMenge(Math.max(1, Math.round(v)))}
              ariaLabel="Stückzahl"
            />
          </div>
        </div>

        {/* NumPad-Footer — gemountet solange aktiv oder im Schließen (Exit-Animation) */}
        {padMounted && (
          <div
            ref={footerRef}
            data-testid="kalk-numpad"
            className="shrink-0 overflow-hidden px-5"
            style={{
              // Öffnen: Transform-Slide (translateY 100%→0) — Tasten in voller Höhe, kein
              // Clipping → zuverlässige Eingabe. Schließen: max-height kollabiert von der
              // gemessenen Höhe auf 0 — Box + belegter Platz fahren SIMULTAN nach unten weg
              // (kein stehenbleibender Kasten, kein Pop). Eine kombinierte Transition deckt beides ab.
              maxHeight: closingPad ? '0px' : (padH ? `${padH}px` : '600px'),
              transform: padEntered ? 'translateY(0)' : 'translateY(100%)',
              transition: `transform 0.3s ${IOS_EASE}, max-height 0.3s ${IOS_EASE}`,
              boxShadow: 'inset 0 0.5px 0 var(--separator)',
            }}
          >
            <div ref={contentRef} className="pt-1.5 pb-3">
              <div className="flex justify-end">
                <button
                  type="button"
                  data-testid="kalk-numpad-done"
                  onClick={closeNumpad}
                  className="px-1 py-2 text-[16px] font-semibold text-[var(--accent-online)]"
                >
                  Fertig
                </button>
              </div>
              <SaleNumpad
                key={padField}
                hideDisplay
                prefill={numpadSeed}
                allowComma={padField !== 'menge'}
                onValueChange={writeActive}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
